import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import Breadcrum from '../../components/breadcrum'
import { toast } from 'react-hot-toast'
import {
  createTrending,
  updateTrending,
  getTrendingById,
} from '../../api/trendingApi'

const AddEditTrendingNews = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditMode = Boolean(id)
  const [imagePreview, setImagePreview] = useState(null)

  useEffect(() => {
    document.title = `${isEditMode ? 'Edit ' : 'Add '}Trending News`
  }, [isEditMode])

  // Initialize form with default values
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      tagUrl: '',
      pageUrl: '',
      image: null,
    },
  })

  // Watch the image field to access it in the submission
  const imageField = watch('image')

  // Fetch trending news data if in edit mode
  const { data: trendingData, isLoading: isLoadingTrending } = useQuery({
    queryKey: ['trending', id],
    queryFn: () => getTrendingById(id),
    enabled: isEditMode,
    onSuccess: (data) => {
      console.log('Query success handler triggered with data:', data)
      populateForm(data)
    },
    onError: (error) => {
      toast.error('Failed to load trending news data')
      console.error('Error loading trending news:', error)
      navigate('/dealdashboard/news') // Redirect back to trending list on error
    },
  })

  // Separate function to handle form population to ensure it runs
  const populateForm = (data) => {
    if (!data) {
      console.error('No data available to populate form')
      return
    }

    console.log('Populating form with data:', data)

    // Use timeout to ensure this runs after render cycle
    setTimeout(() => {
      // Explicitly set each form field
      setValue('title', data.title || '')
      setValue('description', data.description || '')
      setValue('tagUrl', data.tagUrl || '')
      setValue('pageUrl', data.pageUrl || '')

      // Set image preview if available
      if (data.imageUrl) {
        setImagePreview(data.imageUrl)
      }

      console.log('Form populated with values', {
        title: data.title,
        description: data.description,
        tagUrl: data.tagUrl,
        pageUrl: data.pageUrl,
        imageSet: !!data.imageUrl,
      })
    }, 0)
  }

  // Ensure form is populated when data changes
  useEffect(() => {
    if (isEditMode && trendingData) {
      console.log('trendingData updated in useEffect:', trendingData)
      populateForm(trendingData)
    }
  }, [trendingData, isEditMode])

  // Create image preview when image field changes
  useEffect(() => {
    if (imageField && imageField[0]) {
      const file = imageField[0]
      const previewUrl = URL.createObjectURL(file)
      setImagePreview(previewUrl)

      // Clean up the URL when component unmounts or image changes
      return () => URL.revokeObjectURL(previewUrl)
    }
  }, [imageField])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createTrending,
    onSuccess: () => {
      toast.success('Trending news added successfully!')
      reset()
      setImagePreview(null)
      navigate('/dealdashboard/news') // Redirect to trending list
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(
        error.response?.data?.message || 'Failed to add trending news'
      )
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateTrending(id, data),
    onSuccess: () => {
      toast.success('Trending news updated successfully!')
      navigate('/dealdashboard/news') // Redirect to trending list
    },
    onError: (error) => {
      console.error('Error details:', error)
      toast.error(
        error.response?.data?.message || 'Failed to update trending news'
      )
    },
  })

  const handleImageChange = (e) => {
    const files = e.target.files
    if (files && files.length > 0) {
      // Set the file to the form
      setValue('image', files)
    }
  }

  const onSubmit = (data) => {
    console.log('Form submitted with data:', data)

    // In edit mode, image is optional
    if (!isEditMode && (!data.image || !data.image[0])) {
      toast.error('Please upload an image')
      return
    }

    // Submit the form data based on mode
    if (isEditMode) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  // Debug form values
  const formValues = watch()
  useEffect(() => {
    if (isEditMode) {
      console.log('Current form values:', formValues)
    }
  }, [formValues, isEditMode])

  // Show loading state while fetching data in edit mode
  if (isEditMode && isLoadingTrending) {
    return (
      <>
        <Breadcrum heading="Trending" subheading="Edit Trending News" />
        <div className="max-w-8xl text-center mx-auto px-4 py-6">
          <p className="text-gray-600">Loading trending news data...</p>
        </div>
      </>
    )
  }

  return (
    <>
      <Breadcrum
        heading="Trending"
        subheading={isEditMode ? 'Edit Trending News' : 'Add Trending News'}
      />
      <div className="max-w-8xl text-left mx-auto px-4 py-6">
        {isEditMode && trendingData && (
          <div className="bg-blue-50 p-4 mb-6 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-800">Editing Article:</h3>
            <p className="text-blue-700">{trendingData.title}</p>
          </div>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Heading Input */}
          <div className="bg-white p-6 rounded-xl shadow mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Trending News Heading
            </h2>
            <input
              type="text"
              placeholder="Enter headline..."
              className={`border ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
              defaultValue={trendingData?.title || ''}
              {...register('title', {
                required: 'Headline is required',
              })}
            />
            {errors.title && (
              <p className="mt-1 text-red-500 text-sm">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Image Upload */}
          <div className="bg-white p-6 rounded-xl shadow mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Trending News Image
            </h2>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="mt-4 max-h-64 object-cover rounded-lg border border-gray-200"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="mt-3 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-600 hover:file:bg-orange-100"
              {...register('image', {
                required: isEditMode ? false : 'Image is required',
              })}
            />
            {errors.image && (
              <p className="mt-1 text-red-500 text-sm">
                {errors.image.message}
              </p>
            )}
            {isEditMode && (
              <p className="mt-1 text-gray-500 text-sm">
                Upload a new image only if you want to replace the current one.
              </p>
            )}
          </div>

          {/* Content Area */}
          <div className="bg-white p-6 rounded-xl shadow mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Trending News Content
            </h2>
            <textarea
              rows="5"
              placeholder="Write the news content here..."
              className={`border ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
              defaultValue={trendingData?.description || ''}
              {...register('description', {
                required: 'Content is required',
              })}
            ></textarea>
            {errors.description && (
              <p className="mt-1 text-red-500 text-sm">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* URLs */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Tag Section URL
              </h2>
              <input
                type="text"
                placeholder="e.g. /politics"
                className={`border ${
                  errors.tagUrl ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                defaultValue={trendingData?.tagUrl || ''}
                {...register('tagUrl', {
                  required: false,
                })}
              />
              {errors.tagUrl && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.tagUrl.message}
                </p>
              )}
            </div>
            <div className="bg-white p-6 rounded-xl shadow">
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                Page Redirect URL
              </h2>
              <input
                type="text"
                placeholder="e.g. /news-details"
                className={`border ${
                  errors.pageUrl ? 'border-red-500' : 'border-gray-300'
                } rounded-lg p-3 w-full text-base focus:border-orange-500 focus:ring-orange-500 focus:outline-none`}
                defaultValue={trendingData?.pageUrl || ''}
                {...register('pageUrl', {
                  required: false,
                })}
              />
              {errors.pageUrl && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.pageUrl.message}
                </p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="bg-orange-500 hover:bg-orange-600 text-white text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all disabled:bg-orange-300 disabled:cursor-not-allowed"
            >
              {createMutation.isPending || updateMutation.isPending
                ? isEditMode
                  ? 'Updating News...'
                  : 'Adding News...'
                : isEditMode
                ? 'Update News'
                : 'Add News'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dealdashboard/news')}
              className="ml-4 bg-gray-100 hover:bg-gray-200 text-gray-800 text-lg font-medium px-8 py-3 rounded-lg shadow-md transition-all"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default AddEditTrendingNews
